import * as Sentry from '@sentry/browser';

import {sendTrackingMessage} from '../TrackingMessage';
import {TRACKING_EVENT_TYPE} from './Constants';

import type {
	OldTimeRegistrationTargetSuggestions,
	TimeRegistrationTargetSuggestions,
} from '../../forecast-app/my-work-tab/my-timesheets-page/time-registration-target-suggestions/TimeRegistrationTargetSuggestionsFetch';
import {toEntityId} from '../../forecast-app/shared/components/modals/time-registration/time-registration-modal/util/TimeRegistrationModalUtil';
import {TimeRegistrationEntity} from '../../forecast-app/shared/components/modals/time-registration/time-registration-modal/types/TimeRegistrationEntityId';

type FormattedTimeRegistrationTargetSuggestion = {
	id: number;
	entity: TimeRegistrationEntity;
	rank: number;
	score: number;
};
function formatTimeRegistrationSuggestions(
	timeRegistrationSuggestions: TimeRegistrationTargetSuggestions
): FormattedTimeRegistrationTargetSuggestion[] | null {
	if (!timeRegistrationSuggestions) return null;
	return timeRegistrationSuggestions.reduce((acc: FormattedTimeRegistrationTargetSuggestion[], suggestion) => {
		if (suggestion) {
			const entityId = toEntityId({
				taskId: suggestion.task?.id || null,
				projectId: suggestion.project?.id || null,
				idleTimeId: suggestion.idleTime?.id || null,
			});
			if (entityId && suggestion.rank && suggestion.total) {
				acc.push({
					id: entityId.id,
					entity: entityId.entity,
					rank: suggestion.rank,
					score: suggestion.total,
				});
			}
		}
		return acc;
	}, []);
}

function formatOldTimeRegistrationSuggestions(
	timeRegistrationSuggestions: OldTimeRegistrationTargetSuggestions
): FormattedTimeRegistrationTargetSuggestion[] | null {
	if (!timeRegistrationSuggestions) return null;
	return timeRegistrationSuggestions.reduce((acc: FormattedTimeRegistrationTargetSuggestion[], suggestion) => {
		if (suggestion) {
			const entityId = toEntityId({
				taskId: suggestion.task?.id || null,
				projectId: null,
				idleTimeId: suggestion.idleTime?.id || null,
			});
			if (entityId && suggestion.total) {
				acc.push({
					id: entityId.id,
					entity: entityId.entity,
					score: suggestion.total,
					rank: -1,
				});
			}
		}
		return acc;
	}, []);
}

type TimeRegistrationTargetSuggestionFeedbackInput = {
	companyId: string;
	viewerId: string;
	personId: string;
	uiSource: string;
	modelVersion: string;
	timeRegistrationTargetTaskId?: string | null;
	timeRegistrationTargetProjectId?: string | null;
	timeRegistrationTargetIdleTimeId?: string | null;
	timeRegistrationDate: string;
	timeRegistrationTargetSuggestions?: TimeRegistrationTargetSuggestions;
	oldTimeRegistrationTargetSuggestions?: OldTimeRegistrationTargetSuggestions;
};
function constructFeedbackBody({
	companyId,
	viewerId,
	personId,
	uiSource,
	modelVersion,
	timeRegistrationTargetTaskId = null,
	timeRegistrationTargetProjectId = null,
	timeRegistrationTargetIdleTimeId = null,
	timeRegistrationDate,
	timeRegistrationTargetSuggestions,
	oldTimeRegistrationTargetSuggestions,
}: TimeRegistrationTargetSuggestionFeedbackInput) {
	const timeRegistrationEntityId = toEntityId({
		taskId: timeRegistrationTargetTaskId,
		projectId: timeRegistrationTargetProjectId,
		idleTimeId: timeRegistrationTargetIdleTimeId,
	});
	if (!timeRegistrationEntityId) return null;
	return {
		eventType: TRACKING_EVENT_TYPE.TIME_REGISTRATION_TARGET_SUGGESTION_FEEDBACK,
		companyId,
		viewerId,
		personId,
		uiSource,
		modelVersion,
		timeRegistrationTargetEntity: timeRegistrationEntityId.entity,
		timeRegistrationTargetId: timeRegistrationEntityId.id,
		timeRegistrationDate,
		timeRegistrationTargetSuggestions: timeRegistrationTargetSuggestions
			? formatTimeRegistrationSuggestions(timeRegistrationTargetSuggestions)
			: null,
		oldTimeRegistrationTargetSuggestions: oldTimeRegistrationTargetSuggestions
			? formatOldTimeRegistrationSuggestions(oldTimeRegistrationTargetSuggestions)
			: null,
	};
}

export function trackTimeRegistrationTargetSuggestionFeedback(body: TimeRegistrationTargetSuggestionFeedbackInput): void {
	try {
		const s3body = constructFeedbackBody(body);
		if (s3body) {
			sendTrackingMessage(s3body);
		}
	} catch (err) {
		Sentry.captureException(err);
		console.log('Tracking failed, reported to Sentry', err);
	}
}
